import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import type {
  CreateTemplateFormDTO,
  UpdateTemplateFormDTO,
} from "../../domain/contracts";
import type {
  CreateTemplateCategoryDTO,
  UpdateTemplateCategoryDTO,
} from "../../domain/contracts/category";
import { templateApi } from "./client";

export const templateKeys = {
  all: ["template"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["template", "list", q] as const,
  listByDepartment: (q: Partial<OffsetPageQueryDTO>) =>
    ["template", "by-department", q] as const,
  detail: (id: string) => ["template", "detail", id] as const,
  versions: (id: string) => ["template", "versions", id] as const,
};

export function useTemplatesQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: templateKeys.list(q),
    queryFn: () => templateApi.list(q),
  });
}

export function useDepartmentsWithTemplatesQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: templateKeys.listByDepartment(q),
    queryFn: () => templateApi.listByDepartment(q),
  });
}

export function useTemplateQuery(id: string) {
  return useQuery({
    queryKey: templateKeys.detail(id),
    queryFn: () => templateApi.get(id),
    enabled: !!id,
  });
}

export function useTemplateVersionsQuery(id: string) {
  return useQuery({
    queryKey: templateKeys.versions(id),
    queryFn: () => templateApi.getVersions(id),
    enabled: !!id,
  });
}

export function useTemplateCategoriesQuery() {
  return useQuery({
    queryKey: ["template", "categories"],
    queryFn: () => templateApi.getCategories(),
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTemplateFormDTO) => templateApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.all });
      toast.success("ສ້າງຮ່າງເອກະສານສຳເລັດ");
    },
    onError: () => {
      toast.error("ສ້າງຮ່າງເອກະສານລົ້ມເຫຼວ");
    },
  });
}

export function useUpdateTemplateFull(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTemplateFormDTO) =>
      templateApi.updateFull(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.all });
      qc.invalidateQueries({ queryKey: templateKeys.detail(id) });
      toast.success("ອັບເດດຮ່າງເອກະສານສຳເລັດ");
    },
    onError: () => {
      toast.error("ອັບເດດຮ່າງເອກະສານລົ້ມເຫຼວ");
    },
  });
}

// Template Category Hooks
export function useCreateTemplateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTemplateCategoryDTO) =>
      templateApi.createCategory(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["template", "categories"] });
      toast.success("ສ້າງປະເພດຮ່າງເອກະສານສຳເລັດ");
    },
    onError: (error: Error) => {
      toast.error(error.message || "ສ້າງປະເພດຮ່າງເອກະສານລົ້ມເຫຼວ");
    },
  });
}

export function useUpdateTemplateCategory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTemplateCategoryDTO) =>
      templateApi.updateCategory(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["template", "categories"] });
      qc.invalidateQueries({ queryKey: ["template", "category", id] });
      toast.success("ອັບເດດປະເພດຮ່າງເອກະສານສຳເລັດ");
    },
    onError: (error: Error) => {
      toast.error(error.message || "ອັບເດດປະເພດຮ່າງເອກະສານລົ້ມເຫຼວ");
    },
  });
}

export function useDeleteTemplateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => templateApi.deleteCategory(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["template", "categories"] });
      toast.success("ລຶບປະເພດຮ່າງເອກະສານສຳເລັດ");
    },
    onError: (error: Error) => {
      toast.error(error.message || "ລຶບປະເພດຮ່າງເອກະສານລົ້ມເຫຼວ");
    },
  });
}
